import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Leaf } from 'lucide-react';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { Field, TextInput } from '@siliconx/shared/ui/Field';
import { cn } from '@siliconx/shared/ui/utils';
import {
  ESG_BUY_USED_EMBODIED_CONVENTION,
  ESG_CAT1_OR_2_HELPER,
  ESG_CAT8_COLO_CONVENTION_NOTE,
  ESG_CATEGORY_LABELS,
  ESG_DISPOSAL_ADDER_BASIS_NOTE,
  ESG_FRAMING_LINE,
  ESG_LOCATION_BASED_METHOD_BADGE,
  ESG_LOCATION_BASED_METHOD_NOTE,
  ESG_TARGET_LENS_HELPER,
  ESG_TARGET_SETTINGS_HELPER,
  SUSTAINABILITY_CONFIG_DEFAULTS,
} from '@siliconx/shared';
import type {
  CarbonTargetDto,
  CarbonTargetOrgWrite,
  CarbonTargetsResponse,
  CarbonTargetType,
  GhgPurchaseCategoryDto,
  SustainabilityConfigRow,
} from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { InfoTooltip } from './InfoTooltip';

/**
 * SIL-439 (W4) — the Sustainability card on the Financials setup surface
 * (`/financials/setup`), per resources/plans/sustainability-forecasting-spec.md
 * §4 + §6 ("the setup card (targets, Cat 1/2 toggle, adder scalars) extends
 * the existing Financials setup surface").
 *
 * Backed entirely by the W3 endpoints in
 * `apps/api/src/routes/client/sustainability.ts` — carbon-target CRUD
 * (`/targets`, `/targets/org`, `/targets/team/:teamId`) and the org config
 * (`/config`). No new API surface.
 *
 * SCOPE NOTE: the ticket text says "org headline + optional data_center/team
 * rows", but `carbon_target.scope_type` is `org|team` ONLY in v1 — migration
 * 718 dropped `data_center` because a data_center is seller_org-owned, not
 * customer_org-owned (see packages/shared/src/dto/sustainability.ts's own
 * scope note). This card therefore edits org + team rows only.
 *
 * RBAC: read is open to any member (mirrors financials/policy.tsx); every
 * write control is admin-only here AND admin-gated server-side (trusted
 * `clientUser.role` + RLS on 718/719). The UI gate is convenience, never the
 * boundary. `GET /api/client/teams` is itself admin-only, so team NAMES only
 * resolve for admins — a member sees their visible team rows labelled
 * generically.
 *
 * COPY: every helper string is imported verbatim from the SIL-426 inventory
 * constants in `packages/shared/src/dto/esg-export.ts` (§4, §12). Do not
 * inline or paraphrase these — they are legally vetted.
 */

interface TeamSummary {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Form state — strings so a field can be transiently empty while typing.
// ---------------------------------------------------------------------------

/**
 * SIL-474: the ORG target form carries every field either shape can need —
 * only `targetType` decides which subset `toOrgTargetWrite` reads. The
 * TEAM form (`teamDrafts` below) stays the older, plain tCO2e/yr string map
 * since team rows are absolute_budget only (722's `carbon_target_reduction_
 * percent_org_scope_only` CHECK — see `packages/shared/src/dto/
 * sustainability.ts`'s own note on `carbonTargetWriteSchema`).
 */
interface OrgTargetFormState {
  targetType: CarbonTargetType;
  tco2ePerYr: string;
  baseYear: string;
  baseYearTco2e: string;
  targetYear: string;
  reductionPct: string;
  recalcPolicyNote: string;
}

const EMPTY_ORG_TARGET_FORM: OrgTargetFormState = {
  targetType: 'absolute_budget',
  tco2ePerYr: '',
  baseYear: '',
  baseYearTco2e: '',
  targetYear: '',
  reductionPct: '',
  recalcPolicyNote: '',
};

function toOrgTargetForm(row: CarbonTargetDto | null | undefined): OrgTargetFormState {
  if (!row) return EMPTY_ORG_TARGET_FORM;
  return {
    targetType: row.targetType,
    tco2ePerYr: row.tco2ePerYr != null ? String(row.tco2ePerYr) : '',
    baseYear: row.baseYear != null ? String(row.baseYear) : '',
    baseYearTco2e: row.baseYearTco2e != null ? String(row.baseYearTco2e) : '',
    targetYear: row.targetYear != null ? String(row.targetYear) : '',
    reductionPct: row.reductionPct != null ? String(row.reductionPct) : '',
    recalcPolicyNote: row.recalcPolicyNote ?? '',
  };
}

/** `PUT /sustainability/targets/org` body builder — target-type-aware. Reads
 * only the subset of `form` the selected `targetType` needs, mirroring the
 * DB's own `carbon_target_type_fields_coherence` CHECK (722) field-for-field
 * (see `apps/api/src/routes/client/sustainability.ts`'s
 * `orgTargetWriteValues`, the server-side sibling of this function). */
function toOrgTargetWrite(form: OrgTargetFormState): CarbonTargetOrgWrite {
  if (form.targetType === 'reduction_percent') {
    return {
      targetType: 'reduction_percent',
      baseYear: Number(form.baseYear),
      baseYearTco2e: Number(form.baseYearTco2e),
      targetYear: Number(form.targetYear),
      reductionPct: Number(form.reductionPct),
      recalcPolicyNote: form.recalcPolicyNote.trim() === '' ? null : form.recalcPolicyNote.trim(),
    };
  }
  return {
    targetType: 'absolute_budget',
    tco2ePerYr: Number(form.tco2ePerYr),
    baseYear: form.baseYear.trim() === '' ? null : Number(form.baseYear),
    recalcPolicyNote: form.recalcPolicyNote.trim() === '' ? null : form.recalcPolicyNote.trim(),
  };
}

/** Whether the org form has every field its selected `targetType` needs
 * (client-side convenience gate on the Save button — the server/DB are the
 * real boundary, per this file's own RBAC note). */
function isOrgTargetFormValid(form: OrgTargetFormState): boolean {
  if (form.targetType === 'reduction_percent') {
    return (
      form.baseYear.trim() !== '' &&
      form.baseYearTco2e.trim() !== '' &&
      form.targetYear.trim() !== '' &&
      form.reductionPct.trim() !== ''
    );
  }
  return form.tco2ePerYr.trim() !== '';
}

// TEAM target editing (below, `teamDrafts`) stays the pre-existing plain
// tCO2e/yr string map — UNCHANGED by SIL-474 (team rows are absolute_budget
// only, see `OrgTargetFormState`'s own note above).

interface ConfigFormState {
  operated: boolean;
  purchaseGhgCategory: GhgPurchaseCategoryDto;
  disposalWeeeFactor: string;
  usedTransportKm: string;
  /** Displayed as a percentage (2.5), stored as a fraction (0.025). */
  usedRefurbPctDisplay: string;
}

function toConfigForm(row: SustainabilityConfigRow | undefined): ConfigFormState {
  const src = row ?? {
    ...SUSTAINABILITY_CONFIG_DEFAULTS,
    updatedBy: null,
    updatedAt: null,
  };
  return {
    operated: src.operated,
    purchaseGhgCategory: src.purchaseGhgCategory,
    disposalWeeeFactor: String(src.disposalWeeeFactor),
    usedTransportKm: String(src.usedTransportKm),
    usedRefurbPctDisplay: String(round6(src.usedRefurbPct * 100)),
  };
}

/** Guards the fraction<->percent round trip from float dust (0.025*100). */
function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function SectionHeading({
  title,
  helper,
  tooltip,
}: {
  title: string;
  helper: string;
  tooltip?: { label: string; body: string };
}) {
  return (
    <div>
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900">
        {title}
        {tooltip && (
          <InfoTooltip label={tooltip.label}>{tooltip.body}</InfoTooltip>
        )}
      </h3>
      <p className="mt-1 max-w-3xl text-xs leading-relaxed text-zinc-500">{helper}</p>
    </div>
  );
}

function StatusLine({ error, saved }: { error: string | null; saved: boolean }) {
  if (error) return <p className="text-xs text-red-600">{error}</p>;
  if (saved) return <p className="text-xs text-emerald-600">Saved.</p>;
  return null;
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export function SustainabilityCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();

  const targetsQ = useQuery<CarbonTargetsResponse>({
    queryKey: qk.sustainability.targets(),
    queryFn: async () => {
      const res = await api.api.client.sustainability.targets.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as CarbonTargetsResponse;
    },
  });

  const configQ = useQuery<SustainabilityConfigRow>({
    queryKey: qk.sustainability.config(),
    queryFn: async () => {
      const res = await api.api.client.sustainability.config.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as SustainabilityConfigRow;
    },
  });

  // Team names come from the admin-only teams list; a member simply gets no
  // names (see RBAC note in the file header) — never an error state here.
  const teamsQ = useQuery<TeamSummary[]>({
    queryKey: qk.teams(),
    queryFn: async () => {
      const res = await api.api.client.teams.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as TeamSummary[];
    },
    enabled: isAdmin,
  });

  const invalidateTargets = () =>
    queryClient.invalidateQueries({ queryKey: qk.sustainability.targets() });

  // -- Org headline target ---------------------------------------------------

  const [orgForm, setOrgForm] = useState<OrgTargetFormState>(EMPTY_ORG_TARGET_FORM);
  const [orgSaved, setOrgSaved] = useState(false);
  useEffect(() => {
    setOrgForm(toOrgTargetForm(targetsQ.data?.org));
  }, [targetsQ.data?.org]);

  const saveOrg = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.sustainability.targets.org.$put({
        json: toOrgTargetWrite(orgForm),
      });
      await throwIfNotOk(res);
    },
    onSuccess: async () => {
      setOrgSaved(true);
      await invalidateTargets();
    },
  });

  const clearOrg = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.sustainability.targets.org.$delete();
      await throwIfNotOk(res);
    },
    onSuccess: async () => {
      setOrgSaved(false);
      setOrgForm(EMPTY_ORG_TARGET_FORM);
      await invalidateTargets();
    },
  });

  // -- Team targets ----------------------------------------------------------

  const teamRows: CarbonTargetDto[] = targetsQ.data?.teams ?? [];
  const teamNameById = new Map((teamsQ.data ?? []).map((t) => [t.id, t.name]));

  /** Draft tCO2e/yr per team id, seeded from the stored rows. Team rows carry
   * no base year / recalc note in this editor — those are org-headline
   * disclosure metadata (§12); the API accepts nulls for both. */
  const [teamDrafts, setTeamDrafts] = useState<Record<string, string>>({});
  useEffect(() => {
    setTeamDrafts(
      Object.fromEntries(teamRows.map((r) => [r.teamId ?? '', String(r.tco2ePerYr)])),
    );
    // Re-seed only when the server rows change identity/value.
  }, [targetsQ.data?.teams]);

  const [teamError, setTeamError] = useState<string | null>(null);
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null);

  async function saveTeamTarget(teamId: string) {
    const raw = teamDrafts[teamId] ?? '';
    setTeamError(null);
    setPendingTeamId(teamId);
    try {
      if (raw.trim() === '') {
        const res = await api.api.client.sustainability.targets.team[':teamId'].$delete({
          param: { teamId },
        });
        await throwIfNotOk(res);
      } else {
        const res = await api.api.client.sustainability.targets.team[':teamId'].$put({
          param: { teamId },
          json: { tco2ePerYr: Number(raw), baseYear: null, recalcPolicyNote: null },
        });
        await throwIfNotOk(res);
      }
      await invalidateTargets();
    } catch (ex: unknown) {
      setTeamError((ex as Error).message);
    } finally {
      setPendingTeamId(null);
    }
  }

  // Admins edit every team; members only ever see the rows their RLS scope
  // returned (no teams list to enumerate from).
  const editableTeams: TeamSummary[] = isAdmin
    ? (teamsQ.data ?? [])
    : teamRows.map((r) => ({
        id: r.teamId ?? '',
        name: teamNameById.get(r.teamId ?? '') ?? 'Team target',
      }));

  // -- Org config (Cat 1/2 toggle + adder scalars) ---------------------------

  const [configForm, setConfigForm] = useState<ConfigFormState>(() => toConfigForm(undefined));
  const [configSaved, setConfigSaved] = useState(false);
  useEffect(() => {
    if (configQ.data) setConfigForm(toConfigForm(configQ.data));
  }, [configQ.data]);

  const saveConfig = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.sustainability.config.$put({
        json: {
          operated: configForm.operated,
          purchaseGhgCategory: configForm.purchaseGhgCategory,
          disposalWeeeFactor: Number(configForm.disposalWeeeFactor),
          usedTransportKm: Number(configForm.usedTransportKm),
          usedRefurbPct: round6(Number(configForm.usedRefurbPctDisplay) / 100),
        },
      });
      await throwIfNotOk(res);
    },
    onSuccess: async () => {
      setConfigSaved(true);
      await queryClient.invalidateQueries({ queryKey: qk.sustainability.config() });
    },
  });

  function resetScalarsToDefaults() {
    setConfigSaved(false);
    setConfigForm((f) => ({
      ...f,
      disposalWeeeFactor: String(SUSTAINABILITY_CONFIG_DEFAULTS.disposalWeeeFactor),
      usedTransportKm: String(SUSTAINABILITY_CONFIG_DEFAULTS.usedTransportKm),
      usedRefurbPctDisplay: String(round6(SUSTAINABILITY_CONFIG_DEFAULTS.usedRefurbPct * 100)),
    }));
  }

  const loading = targetsQ.isLoading || configQ.isLoading;
  const loadError = targetsQ.error ?? configQ.error;

  return (
    <Card as="section" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Leaf className="mt-0.5 size-4 shrink-0 text-emerald-600" />
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Sustainability</h2>
            <p className="mt-1 text-xs text-zinc-500">{ESG_FRAMING_LINE}</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
          {ESG_LOCATION_BASED_METHOD_BADGE}
          <InfoTooltip label="About the location-based method" align="right">
            {ESG_LOCATION_BASED_METHOD_NOTE}
          </InfoTooltip>
        </span>
      </div>

      {loadError && (
        <p className="text-xs text-red-600">
          Failed to load sustainability settings: {(loadError as Error).message}
        </p>
      )}

      {loading ? (
        <p className="text-xs text-zinc-500">Loading sustainability settings…</p>
      ) : (
        <>
          {/* ------------------------------------------------------------ */}
          {/* Organization carbon target (§4, §12)                          */}
          {/* ------------------------------------------------------------ */}
          <section className="space-y-3 border-t border-zinc-100 pt-5">
            <SectionHeading title="Organization carbon target" helper={ESG_TARGET_SETTINGS_HELPER} />

            {/* SIL-474: target-type selector — the org row is the ONLY scope
                that may hold a reduction_percent target (722's org-scope-only
                CHECK); team rows below stay absolute-only. */}
            <fieldset
              className={cn('flex flex-wrap gap-4 text-sm', !isAdmin && 'text-zinc-500')}
              disabled={!isAdmin}
            >
              <legend className="sr-only">Target type</legend>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="carbon-target-type"
                  className="size-4 border-zinc-300 text-brand-600 focus:ring-brand-500"
                  checked={orgForm.targetType === 'absolute_budget'}
                  onChange={() => {
                    setOrgSaved(false);
                    setOrgForm({ ...orgForm, targetType: 'absolute_budget' });
                  }}
                />
                Absolute annual budget
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="carbon-target-type"
                  className="size-4 border-zinc-300 text-brand-600 focus:ring-brand-500"
                  checked={orgForm.targetType === 'reduction_percent'}
                  onChange={() => {
                    setOrgSaved(false);
                    setOrgForm({ ...orgForm, targetType: 'reduction_percent' });
                  }}
                />
                Reduction vs base year
              </label>
            </fieldset>

            {orgForm.targetType === 'absolute_budget' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label="Annual budget (tCO2e/yr)">
                  <TextInput
                    type="number"
                    min={0}
                    step="0.01"
                    value={orgForm.tco2ePerYr}
                    disabled={!isAdmin}
                    placeholder="e.g. 1200"
                    onChange={(e) => {
                      setOrgSaved(false);
                      setOrgForm({ ...orgForm, tco2ePerYr: e.target.value });
                    }}
                  />
                </Field>
                <Field label="Base year (optional)">
                  <TextInput
                    type="number"
                    min={1900}
                    max={2200}
                    step="1"
                    value={orgForm.baseYear}
                    disabled={!isAdmin}
                    placeholder="—"
                    onChange={(e) => {
                      setOrgSaved(false);
                      setOrgForm({ ...orgForm, baseYear: e.target.value });
                    }}
                  />
                </Field>
                <Field label="Recalculation policy note (optional)" className="sm:col-span-1">
                  <TextInput
                    type="text"
                    maxLength={2000}
                    value={orgForm.recalcPolicyNote}
                    disabled={!isAdmin}
                    placeholder="—"
                    onChange={(e) => {
                      setOrgSaved(false);
                      setOrgForm({ ...orgForm, recalcPolicyNote: e.target.value });
                    }}
                  />
                </Field>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Base year">
                  <TextInput
                    type="number"
                    min={1900}
                    max={2200}
                    step="1"
                    value={orgForm.baseYear}
                    disabled={!isAdmin}
                    placeholder="e.g. 2019"
                    onChange={(e) => {
                      setOrgSaved(false);
                      setOrgForm({ ...orgForm, baseYear: e.target.value });
                    }}
                  />
                </Field>
                <Field
                  label="Base-year emissions (tCO2e)"
                  hint="Customer-provided — entered by you, not computed by the platform."
                >
                  <TextInput
                    type="number"
                    min={0}
                    step="0.01"
                    value={orgForm.baseYearTco2e}
                    disabled={!isAdmin}
                    placeholder="e.g. 5000"
                    onChange={(e) => {
                      setOrgSaved(false);
                      setOrgForm({ ...orgForm, baseYearTco2e: e.target.value });
                    }}
                  />
                </Field>
                <Field label="Target year">
                  <TextInput
                    type="number"
                    min={1900}
                    max={2300}
                    step="1"
                    value={orgForm.targetYear}
                    disabled={!isAdmin}
                    placeholder="e.g. 2030"
                    onChange={(e) => {
                      setOrgSaved(false);
                      setOrgForm({ ...orgForm, targetYear: e.target.value });
                    }}
                  />
                </Field>
                <Field label="Reduction (%)">
                  <TextInput
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={orgForm.reductionPct}
                    disabled={!isAdmin}
                    placeholder="e.g. 40"
                    onChange={(e) => {
                      setOrgSaved(false);
                      setOrgForm({ ...orgForm, reductionPct: e.target.value });
                    }}
                  />
                </Field>
                <Field label="Recalculation policy note (optional)" className="sm:col-span-2">
                  <TextInput
                    type="text"
                    maxLength={2000}
                    value={orgForm.recalcPolicyNote}
                    disabled={!isAdmin}
                    placeholder="—"
                    onChange={(e) => {
                      setOrgSaved(false);
                      setOrgForm({ ...orgForm, recalcPolicyNote: e.target.value });
                    }}
                  />
                </Field>
              </div>
            )}

            {/* The Protocol's recalculation-policy expectation, surfaced
                whenever a base year is set — for EITHER target type (copy
                §12 states it for the absolute-budget shape; a
                reduction_percent base year is the same GHG-Protocol
                recalculation-policy trigger, just REQUIRED rather than
                optional to enter). */}
            {orgForm.baseYear.trim() !== '' && orgForm.recalcPolicyNote.trim() === '' && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                A base year is set without a recalculation policy note — the GHG Protocol requires an
                accompanying recalculation policy, and both are included in disclosures when provided.
              </p>
            )}

            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={saveOrg.isPending || !isOrgTargetFormValid(orgForm)}
                  onClick={() => saveOrg.mutate()}
                >
                  {saveOrg.isPending ? 'Saving…' : 'Save organization target'}
                </Button>
                {targetsQ.data?.org && (
                  <button
                    type="button"
                    disabled={clearOrg.isPending}
                    onClick={() => clearOrg.mutate()}
                    className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    {clearOrg.isPending ? 'Removing…' : 'Remove target'}
                  </button>
                )}
                <StatusLine
                  error={
                    (saveOrg.error as Error | null)?.message ??
                    (clearOrg.error as Error | null)?.message ??
                    null
                  }
                  saved={orgSaved}
                />
              </div>
            )}
          </section>

          {/* ------------------------------------------------------------ */}
          {/* Team targets (§4, §12 — never summed)                         */}
          {/* ------------------------------------------------------------ */}
          <section className="space-y-3 border-t border-zinc-100 pt-5">
            <SectionHeading title="Team targets (optional)" helper={ESG_TARGET_LENS_HELPER} />

            {editableTeams.length === 0 ? (
              <p className="text-xs text-zinc-500">
                No teams to set a target on yet.
              </p>
            ) : (
              <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
                {editableTeams.map((team) => {
                  const stored = teamRows.find((r) => r.teamId === team.id);
                  return (
                    <li
                      key={team.id}
                      className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-zinc-800">{team.name}</p>
                        <p className="text-xs text-zinc-400">
                          {stored ? 'Target set' : 'No target'}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <TextInput
                          type="number"
                          min={0}
                          step="0.01"
                          aria-label={`${team.name} annual budget in tCO2e per year`}
                          value={teamDrafts[team.id] ?? ''}
                          disabled={!isAdmin}
                          placeholder="tCO2e/yr"
                          className="w-36"
                          onChange={(e) =>
                            setTeamDrafts((d) => ({ ...d, [team.id]: e.target.value }))
                          }
                        />
                        {isAdmin && (
                          <button
                            type="button"
                            disabled={pendingTeamId === team.id}
                            onClick={() => void saveTeamTarget(team.id)}
                            className="shrink-0 rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                          >
                            {pendingTeamId === team.id
                              ? 'Saving…'
                              : (teamDrafts[team.id] ?? '').trim() === ''
                                ? 'Clear'
                                : 'Save'}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {teamError && <p className="text-xs text-red-600">{teamError}</p>}
          </section>

          {/* ------------------------------------------------------------ */}
          {/* Purchased-hardware category + facility treatment (§4)         */}
          {/* ------------------------------------------------------------ */}
          <section className="space-y-3 border-t border-zinc-100 pt-5">
            <SectionHeading
              title={ESG_CATEGORY_LABELS.cat1_or_2}
              helper={ESG_CAT1_OR_2_HELPER}
            />

            <label
              className={cn(
                'flex items-start gap-2 text-sm text-zinc-800',
                !isAdmin && 'text-zinc-500',
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 size-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                disabled={!isAdmin}
                checked={configForm.purchaseGhgCategory === 'cat2'}
                onChange={(e) => {
                  setConfigSaved(false);
                  setConfigForm({
                    ...configForm,
                    purchaseGhgCategory: e.target.checked ? 'cat2' : 'cat1',
                  });
                }}
              />
              <span>
                We capitalize hardware purchases
                <span className="ml-2 text-xs text-zinc-500">
                  (reports purchased hardware under Category{' '}
                  {configForm.purchaseGhgCategory === 'cat2' ? '2' : '1'})
                </span>
              </span>
            </label>

            <label
              className={cn(
                'flex items-start gap-2 text-sm text-zinc-800',
                !isAdmin && 'text-zinc-500',
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 size-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
                disabled={!isAdmin}
                checked={configForm.operated}
                onChange={(e) => {
                  setConfigSaved(false);
                  setConfigForm({ ...configForm, operated: e.target.checked });
                }}
              />
              <span>
                We operate our own facilities
                <span className="ml-2 text-xs text-zinc-500">
                  (electricity reports under{' '}
                  {configForm.operated ? ESG_CATEGORY_LABELS.scope2 : ESG_CATEGORY_LABELS.cat8})
                </span>
              </span>
            </label>
            <p className="max-w-3xl text-xs leading-relaxed text-zinc-500">
              {ESG_CAT8_COLO_CONVENTION_NOTE}
            </p>
          </section>

          {/* ------------------------------------------------------------ */}
          {/* Adder scalars (§2, §3 — org overrides of the SIL-420 defaults) */}
          {/* ------------------------------------------------------------ */}
          <section className="space-y-3 border-t border-zinc-100 pt-5">
            <SectionHeading
              title="Adder scalars"
              helper={ESG_BUY_USED_EMBODIED_CONVENTION}
              tooltip={{ label: 'About the disposal adder', body: ESG_DISPOSAL_ADDER_BASIS_NOTE }}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field
                label="Disposal factor (kgCO2e/tonne)"
                hint={`Default ${SUSTAINABILITY_CONFIG_DEFAULTS.disposalWeeeFactor}`}
              >
                <TextInput
                  type="number"
                  min={0}
                  step="0.01"
                  value={configForm.disposalWeeeFactor}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    setConfigSaved(false);
                    setConfigForm({ ...configForm, disposalWeeeFactor: e.target.value });
                  }}
                />
              </Field>
              <Field
                label="Used-purchase transport (km)"
                hint={`Default ${SUSTAINABILITY_CONFIG_DEFAULTS.usedTransportKm}`}
              >
                <TextInput
                  type="number"
                  min={0}
                  step="1"
                  value={configForm.usedTransportKm}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    setConfigSaved(false);
                    setConfigForm({ ...configForm, usedTransportKm: e.target.value });
                  }}
                />
              </Field>
              <Field
                label="Refurbishment adder (% of embodied)"
                hint={`Default ${round6(SUSTAINABILITY_CONFIG_DEFAULTS.usedRefurbPct * 100)}%`}
              >
                <TextInput
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  value={configForm.usedRefurbPctDisplay}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    setConfigSaved(false);
                    setConfigForm({ ...configForm, usedRefurbPctDisplay: e.target.value });
                  }}
                />
              </Field>
            </div>

            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={saveConfig.isPending}
                  onClick={() => saveConfig.mutate()}
                >
                  {saveConfig.isPending ? 'Saving…' : 'Save sustainability settings'}
                </Button>
                <button
                  type="button"
                  onClick={resetScalarsToDefaults}
                  className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
                >
                  Reset scalars to defaults
                </button>
                <StatusLine
                  error={(saveConfig.error as Error | null)?.message ?? null}
                  saved={configSaved}
                />
              </div>
            )}
          </section>

          {!isAdmin && (
            <p className="border-t border-zinc-100 pt-4 text-xs text-zinc-500">
              Only an organization admin can change these settings.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
